import { useMemo, useState } from 'react';
import { useAction, useApiQuery, useResourceList } from '../../engine/api/hooks';
import { Badge, Chip, EmptyState, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Select } from '../../engine/design/primitives/Input';
import { Icon } from '../../engine/design/icons';
import { formatDateTime } from '../../lib/format';

// Scheduling from the ticket (docs/servicedesk-v2-proposal.md P3): either book a slot on the
// client's behalf while they are on the phone, or send them a link and let them pick. Both end the
// same way — an appointment on the ticket, the status moved, the SLA held.

interface AppointmentType { id: string; key: string; name: string; description: string | null; duration_minutes: number; location: string; is_active: boolean }
interface Slot { start: string; end: string; technician_id: string; technician_name: string }
interface Appointment { id: string; starts_at: string; ends_at: string; status: string; location: string; technician_id: string; subject: string | null }

const DAY_LABEL = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const TIME_LABEL = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export function SchedulePanel({ ticketId, onBooked }: { ticketId: string; onBooked?: () => void }) {
  const types = useResourceList<AppointmentType>('appointment-types', { sort: 'name' }, { limit: 50 });
  const active = types.rows.filter((t) => t.is_active);
  const [typeId, setTypeId] = useState('');
  const chosen = active.find((t) => t.id === typeId) ?? active[0] ?? null;

  const availability = useApiQuery<{ slots: Slot[] }>(
    ['availability', chosen?.id ?? ''],
    `/v1/appointments/availability?type_id=${chosen?.id ?? ''}`,
    { enabled: Boolean(chosen) },
  );
  const booked = useApiQuery<{ data: Appointment[] }>(['ticket-appointments', ticketId], `/v1/appointments?filter[ticket_id][eq]=${ticketId}&sort=starts_at`, { enabled: Boolean(ticketId) });
  const book = useAction('appointments', 'book', { toast: { success: 'Booked' } });
  const invite = useAction<{ url: string }>('booking-requests', 'invite', { toast: { success: 'Booking link sent' } });
  const cancel = useAction('appointments', 'cancel', { toast: { success: 'Cancelled' } });
  const [link, setLink] = useState<string | null>(null);

  // Group the offer by day: a flat list of forty times is unreadable, a week of days is not.
  const byDay = useMemo(() => {
    const days = new Map<string, Slot[]>();
    for (const s of availability.data?.slots ?? []) {
      const key = s.start.slice(0, 10);
      if (!days.has(key)) days.set(key, []);
      if (days.get(key)!.length < 12) days.get(key)!.push(s);
    }
    return [...days.entries()].slice(0, 5);
  }, [availability.data]);

  const refresh = () => {
    void availability.refetch();
    void booked.refetch();
    onBooked?.();
  };

  const upcoming = (booked.data?.data ?? []).filter((a) => a.status === 'booked');

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      {upcoming.length > 0 && (
        <div className="hf-stack" style={{ gap: 'var(--s-1)' }}>
          <div className="hf-caps">Booked</div>
          {upcoming.map((a) => (
            <div key={a.id} className="hf-between">
              <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
                <Icon name="calendar" size={12} />
                <span className="hf-num">{formatDateTime(a.starts_at)}</span>
                <Badge tone="accent" dot={false}>{a.location}</Badge>
              </span>
              <Button size="sm" variant="ghost" icon="x" onClick={() => cancel.mutate({ id: a.id, body: { reason: 'Cancelled from the ticket' } }, { onSuccess: refresh })}>
                Cancel
              </Button>
            </div>
          ))}
        </div>
      )}

      {active.length === 0 ? (
        <EmptyState title="No appointment types are set up" icon="calendar" />
      ) : (
        <>
          <Field label="What to book" id="sd-appt-type">
            <Select id="sd-appt-type" size="sm" value={chosen?.id ?? ''} onChange={(e) => setTypeId(e.target.value)}
              options={active.map((t) => ({ value: t.id, label: `${t.name} · ${t.duration_minutes} min` }))} />
          </Field>

          <div className="hf-row">
            <Button size="sm" variant="outline" icon="send" busy={invite.isPending}
              onClick={() => invite.mutate({ body: { type_id: chosen!.id, ticket_id: ticketId } }, { onSuccess: (r) => { setLink((r as { url: string }).url); refresh(); } })}>
              Send a booking link
            </Button>
            {link && <span className="hf-mono hf-muted" style={{ fontSize: 'var(--t-11)' }}>{link}</span>}
          </div>

          <div className="hf-caps">Or book it now</div>
          {availability.isLoading ? (
            <Skeleton lines={3} />
          ) : byDay.length === 0 ? (
            <EmptyState title="Nothing free in the booking window" icon="calendar" />
          ) : (
            <div className="hf-stack" style={{ gap: 'var(--s-2)' }}>
              {byDay.map(([day, slots]) => (
                <div key={day} className="hf-stack" style={{ gap: 'var(--s-1)' }}>
                  <span className="hf-muted" style={{ fontSize: 'var(--t-11)' }}>{DAY_LABEL(day)}</span>
                  <div className="hf-chiprow">
                    {slots.map((s) => (
                      <Chip key={s.start} icon="clock" onClick={() => book.mutate({ body: { type_id: chosen!.id, ticket_id: ticketId, technician_id: s.technician_id, starts_at: s.start } }, { onSuccess: refresh })}>
                        {TIME_LABEL(s.start)} <span className="hf-muted">{s.technician_name}</span>
                      </Chip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
